/**
 * InvestmentByCategoryChart.tsx
 *
 * Horizontal stacked bar chart showing investment per label category.
 * Four bars: energetisch / esthetisch / veiligheid / algemeen.
 * Each bar split into planned (lighter) + actueel (darker) segments.
 *
 * Clicking a bar calls onCategoryClick so the page can filter the table
 * by that label category.
 */
import { Bar } from "react-chartjs-2";
import { ChartEvent, ActiveElement } from "chart.js";
import { Gauge } from "@phosphor-icons/react";
import { EPCMetrics, InvestmentByCategory } from "../hooks/useEnergyPerformanceData";
import { InvestmentMode } from "./EnergyHeroKpiCards";

// Color tokens matching the spec (hex values for Chart.js — not Tailwind classes)
const CATEGORY_COLORS: Record<
  keyof InvestmentByCategory,
  { planned: string; actual: string }
> = {
  energetisch: { planned: "#f5b5a4", actual: "#EA8162" }, // brick light + brick
  veiligheid: { planned: "#fde68a", actual: "#f59e0b" }, // yellow-200 + yellow-500
  esthetisch: { planned: "#b8cc73", actual: "#5B7304" }, // moss light + moss
  algemeen: { planned: "#c4cac8", actual: "#838C89" }, // neutral light + neutral-mid
};

const CATEGORY_LABELS: Record<keyof InvestmentByCategory, string> = {
  energetisch: "Energetisch",
  veiligheid: "Veiligheid",
  esthetisch: "Esthetisch",
  algemeen: "Algemeen",
};

const CATEGORIES = [
  "energetisch",
  "veiligheid",
  "esthetisch",
  "algemeen",
] as const;

interface InvestmentByCategoryChartProps {
  metrics: EPCMetrics | null;
  investmentMode: InvestmentMode;
  onCategoryClick: (category: keyof InvestmentByCategory) => void;
}

export function InvestmentByCategoryChart({
  metrics,
  investmentMode,
  onCategoryClick,
}: InvestmentByCategoryChartProps) {
  const invested = metrics?.investedByCategory;

  const plannedData = CATEGORIES.map(
    (cat) => invested?.[ cat]?.planned ?? 0,
  );
  const actualData = CATEGORIES.map(
    (cat) => invested?.[cat]?.actual ?? 0,
  );

  const labels = CATEGORIES.map((cat) => CATEGORY_LABELS[cat]);

  const chartData = {
    labels,
    datasets: [
      {
        label: "Gepland",
        data: plannedData,
        backgroundColor: CATEGORIES.map(
          (cat) => CATEGORY_COLORS[cat].planned,
        ),
        borderRadius: 2,
        stack: "investment",
      },
      {
        label: "Actueel",
        data: actualData,
        backgroundColor: CATEGORIES.map(
          (cat) => CATEGORY_COLORS[cat].actual,
        ),
        borderRadius: 2,
        stack: "investment",
      },
    ],
  };

  // Show only the mode the user cares about; hide the other stack
  const visibleData = {
    ...chartData,
    datasets:
      investmentMode === "planned"
        ? [chartData.datasets[0]]
        : [chartData.datasets[1]],
  };

  const handleClick = (_event: ChartEvent, elements: ActiveElement[]) => {
    if (elements.length > 0) {
      const index = elements[0].index;
      const category = CATEGORIES[index];
      onCategoryClick(category);
    }
  };

  return (
    <div className="border-neutral-primary/10 rounded-lg border bg-white shadow-sm">
      <div className="border-neutral-primary/10 border-b p-4">
        <h3 className="text-neutral-primary flex items-center gap-2 text-base font-semibold">
          <Gauge
            className="text-moss-primary h-5 w-5"
            weight="duotone"
          />
          Investering per Labelcategorie
        </h3>
        <p className="text-neutral-secondary-mid text-sm">
          Klik op een balk om de tabel te filteren
        </p>
      </div>
      <div className="p-4">
        <div className="h-[280px]">
          <Bar
            data={visibleData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              indexAxis: "y" as const,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (context: any) => {
                      const value = context.raw as number;
                      return `€ ${new Intl.NumberFormat("nl-BE").format(value)}`;
                    },
                  },
                },
              },
              scales: {
                x: {
                  stacked: true,
                  grid: { color: "rgba(0,0,0,0.05)" },
                  ticks: {
                    callback: (value: any) => {
                      if (value >= 1_000_000)
                        return `€${(value / 1_000_000).toFixed(1)}M`;
                      if (value >= 1_000)
                        return `€${(value / 1_000).toFixed(0)}k`;
                      return `€${value}`;
                    },
                  },
                },
                y: {
                  stacked: true,
                  grid: { display: false },
                },
              },
              onClick: handleClick,
            }}
          />
        </div>
      </div>
    </div>
  );
}
